import React from "react";
import { Form } from "react-bootstrap";
import { convertTo12Hour } from "./bookingStepperUtils";

// "Ali Khan (2026_10_DT_12), Sara (...)" - who holds the bookings a slot overlaps.
export const describeBookings = (slot) =>
  (slot?.bookings || [])
    .map((b) => `${b.customer_name || "another customer"}${b.display_booking_id ? ` (${b.display_booking_id})` : ""}`)
    .join(", ");

const bookingsText = (slot) =>
  `${slot.bookings.length} booking${slot.bookings.length === 1 ? "" : "s"} at this time: ${describeBookings(slot)}.`;

// What to tell the vendor about a slot, or "" when there is nothing to say.
// crewConstrained: the service has providers with shifts, so a blue ("info") slot means nobody is on shift
// then (before the shifts start, after they end, or everyone on a break); without that setup it only
// means "there are bookings here".
export const slotMessage = (slot, crewConstrained = false) => {
  if (!slot) return "";
  if (slot.status === "full") {
    const who = describeBookings(slot);
    return `Already booked${who ? ` for ${who}` : ""}. You can still book this time.`;
  }
  if (slot.status === "partial") return `${slot.booked} of ${slot.capacity} providers are booked at this time.`;
  if (slot.status === "info") {
    const hasBookings = slot.bookings?.length > 0;
    if (!crewConstrained) return hasBookings ? bookingsText(slot) : "";
    const base = "No provider is on shift at this time (or everyone is on a break). You can still book it.";
    return hasBookings ? `${base} ${bookingsText(slot)}` : base;
  }
  if (slot.status === "after_hours") return "The job would run past closing time.";
  return "";
};

// The part of the day a time belongs to.
const partOf = (time) => (time < "12:00" ? "Morning" : time < "17:00" ? "Afternoon" : "Evening");
const PARTS = ["Morning", "Afternoon", "Evening"];

const subtitle = (slot, crewConstrained) => {
  if (slot.status === "full") return "Booked";
  if (slot.status === "partial") return `${slot.booked} of ${slot.capacity} booked`;
  if (slot.status === "info" && crewConstrained) return "No one on shift";
  if (slot.status === "info" && slot.bookings?.length) return `${slot.bookings.length} booked`;
  if (slot.status === "after_hours") return "After hours";
  return "";
};

/**
 * Pick the start time of a booking from the vendor's own slots - no service provider needed.
 *   data      { slots, closed, closed_reason, slot_minutes } from vendor_time_slots (null while loading / on error)
 *   value     the chosen time, "HH:MM" ("" = none yet)
 *   onChange  (time "HH:MM") => void
 * A slot that is fully booked stays selectable: it is shown in a different colour with who holds it,
 * and the vendor decides. "Other time" takes any time the grid does not offer.
 */
export default function TimeSlotPicker({ data, loading, error, value: rawValue, onChange, disabled }) {
  const value = String(rawValue || "").slice(0, 5); // a stored time may carry seconds
  const slots = data?.slots || [];
  const selected = slots.find((s) => s.time === value) || null;
  const offGrid = value && !selected;

  const crewConstrained = !!data?.crew_constrained;
  const hasInfoSlot = slots.some((s) => s.status === "info");
  const note = (slot) => slotMessage(slot, crewConstrained);

  const grouped = PARTS.map((part) => ({ part, items: slots.filter((s) => partOf(s.time) === part) })).filter((g) => g.items.length);

  return (
    <div className="bstep-times">
      {loading && <div className="bstep-muted" role="status">Loading times…</div>}

      {!loading && error && (
        <div className="alert alert-warning py-2 mb-2" role="alert" style={{ fontSize: 13 }}>
          {error} You can still enter a time below.
        </div>
      )}

      {!loading && data?.closed && (
        <div className="alert alert-warning py-2 mb-2" role="alert" style={{ fontSize: 13 }}>
          {data.closed_reason || "Closed on this day"}
        </div>
      )}

      {!loading && !error && !data?.closed && slots.length === 0 && data && (
        <div className="bstep-muted mb-2">No times to offer for this day.</div>
      )}

      {!loading &&
        grouped.map((g) => (
          <div key={g.part} className="mb-2">
            <div className="bstep-sum-label">{g.part}</div>
            <div className="bstep-slot-grid" role="group" aria-label={`${g.part} times`}>
              {g.items.map((s) => {
                const isSelected = s.time === value;
                const message = note(s);
                return (
                  <button
                    key={s.time}
                    type="button"
                    className={`bstep-slot is-${s.status}${isSelected ? " is-selected" : ""}`}
                    aria-pressed={isSelected}
                    disabled={disabled || s.status === "after_hours"}
                    title={message || undefined}
                    onClick={() => onChange(s.time)}
                  >
                    <span className="bstep-slot-time">{convertTo12Hour(s.time)}</span>
                    {subtitle(s, crewConstrained) && <span className="bstep-slot-sub">{subtitle(s, crewConstrained)}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

      {!loading && slots.length > 0 && (
        <div className="bstep-slot-legend" aria-hidden="true">
          <span className="is-available">Free</span>
          <span className="is-partial">Some providers booked</span>
          <span className="is-full">Fully booked (can still be chosen)</span>
          {hasInfoSlot && <span className="is-info">{crewConstrained ? "No provider on shift / on break" : "Has bookings"}</span>}
        </div>
      )}

      {offGrid && (
        <div className="mb-2">
          <button type="button" className="bstep-slot is-available is-selected" aria-pressed="true" disabled={disabled}>
            <span className="bstep-slot-time">{convertTo12Hour(value)}</span>
            <span className="bstep-slot-sub">Custom time</span>
          </button>
        </div>
      )}

      {/* what the vendor needs to know about the chosen slot */}
      {selected && note(selected) && (
        <div
          className={`bstep-slot-note ${selected.status === "full" ? "is-warning" : "is-info"}`}
          role={selected.status === "full" ? "alert" : "status"}
          data-testid="slot-note"
        >
          {note(selected)}
        </div>
      )}

      <div className="bstep-other-time">
        <Form.Label htmlFor="bstep-other-time" className="mb-1" style={{ fontSize: 13 }}>
          Other time
        </Form.Label>
        <Form.Control
          id="bstep-other-time"
          type="time"
          step={300}
          value={value || ""}
          disabled={disabled}
          onChange={(e) => e.target.value && onChange(e.target.value.slice(0, 5))}
          style={{ maxWidth: 160, height: 40 }}
        />
      </div>
    </div>
  );
}
